// src/types/user.ts - Types multi-tenant avec permissions inventaires et profils de rôle personnalisés

import type { AppLocale } from '../i18n/types';
import type { UserPushPrefs } from './pushPrefs';

// ✅ NOUVEAU : Interface pour les profils de rôle personnalisés
export interface RoleProfile {
  id: string;
  businessId: string;                    // Propriété du business
  name: string;                          // "Responsable Marketing", "Chef de Salle"
  description?: string;                  // Description optionnelle
  permissions: RolePermissions;          // Matrice complète de permissions
  isActive: boolean;                     // Peut être désactivé
  isDefault: boolean;                    // Profil par défaut (non supprimable)
  createdAt: Date;
  updatedAt: Date;
  createdBy: string;                    // userId de l'admin qui l'a créé
  usageCount?: number;                  // Nombre d'employés utilisant ce profil
}

export function normalizeRolePermissions(input: RolePermissions | undefined): RolePermissions {
  const src: any = input || {};
  const normalized: any = {};
  // On normalise à partir des clés connues (DEFAULT_ADMIN_PERMISSIONS) pour garantir que toute nouvelle permission
  // est explicitement false sur les anciens profils / documents Firestore.
  for (const key of Object.keys(DEFAULT_ADMIN_PERMISSIONS)) {
    normalized[key] = src[key] === true;
  }
  // Compatibilité : un profil autorisé à payer doit aussi pouvoir consulter.
  if (normalized.canPayLicence === true) {
    normalized.canViewLicencePayment = true;
  }
  // Legacy canManageInvoicing → view + update
  if (src.canManageInvoicing === true) {
    normalized.canViewInvoicing = true;
    normalized.canUpdateInvoicing = true;
  }
  // Modifier la facturation implique de pouvoir la consulter.
  if (normalized.canUpdateInvoicing === true) {
    normalized.canViewInvoicing = true;
  }
  // Rétrocompat ramassage : avant canValidatePickup, la vente était ouverte dès l’inbox.
  // Les profils qui changeaient déjà le statut (ou config) gardent la validation.
  if (
    src.canValidatePickup === undefined &&
    (src.canManagePickups === true || src.canUpdatePickupStatus === true)
  ) {
    normalized.canValidatePickup = true;
  }
  return normalized as RolePermissions;
}

// UserProfile avec support multi-business et profils de rôle par business
export interface UserProfile {
  id: string;
  email: string;
  name: string;
  role?: string;  // ✅ NOUVEAU : Rôle métier dans le business (optionnel, SuperAdmin n'en a pas)
  // ✅ Profils de rôle par business (un profil différent par business)
  roleProfileIds?: { [businessId: string]: string };  // { "businessId1": "roleProfileId1", "businessId2": "roleProfileId2" }
  isSuperAdmin: boolean;                  // Flag pour SuperAdmin (cas spécial)
  isCSM: boolean;                         // Customer Success Manager (accès admin avec restrictions)
  businessIds: string[];  // Liste des business accessibles
  currentBusinessId?: string;  // Business sélectionné dans la session actuelle
  employeeIds?: { [businessId: string]: string };  // ✅ NOUVEAU : employeeIds par business (remplace employeeId)
  /** Préférence UI FR/EN (i18n). Optionnel — défaut applicatif = fr. */
  preferredLocale?: AppLocale;
  /**
   * Tours produit déjà vus (clés stables, ex. help_v1).
   * Absence = jamais vu → on peut proposer le guide.
   */
  seenTours?: Partial<Record<string, boolean>>;
  /**
   * Préférences push téléphone (PWA / FCM).
   * Absence = push ON pour la whitelist (rétrocompat Phase 1).
   */
  pushPrefs?: UserPushPrefs;
  isActive: boolean;
  createdAt: Date;
  lastLogin?: Date;
}


export interface RolePermissions {
  // Permissions de base (tous secteurs)
  canViewDashboard: boolean;
  canViewEmployees: boolean;
  canCreateEmployees: boolean;
  canUpdateEmployees: boolean;
  canDeleteEmployees: boolean;
  canViewRoleProfiles: boolean;
  canCreateRoleProfiles: boolean;
  canUpdateRoleProfiles: boolean;
  canDeleteRoleProfiles: boolean;
  
  canViewServices: boolean;
  canCreateServices: boolean;
  canUpdateServices: boolean;
  canDeleteServices: boolean;
  
  canViewPrestations: boolean;
  canCreatePrestations: boolean;
  canUpdatePrestations: boolean;
  canDeletePrestations: boolean;

  /** Annuler (void) une transaction de vente — Caisse (pos) */
  canVoidSales: boolean;
  /** Corriger une vente (void + resaisie guidée) — Caisse (pos) */
  canCorrectSales: boolean;
  /** Émettre un avoir partiel — Caisse (pos) */
  canIssueSaleCredits: boolean;
  
  canManageUsers: boolean; // Permission spéciale pour la gestion des utilisateurs (admin uniquement)
  
  // Permissions Clients (tous secteurs)
  canViewClients: boolean;
  canCreateClients: boolean;
  canUpdateClients: boolean;
  canDeleteClients: boolean;

  // Permissions Paiement en plusieurs
  canViewPaymentPlans: boolean;
  canCreatePaymentPlans: boolean;
  canUpdatePaymentPlans: boolean;
  canCancelPaymentPlans: boolean;
  canCompletePaymentPlans: boolean;       // Clôturer un plan + marquer comme livré
  canAddPaymentPlanPayments: boolean;     // Ajouter des versements partiels sur un plan
  /** Annuler un versement (paiement en plusieurs) */
  canVoidPaymentPlanPayments: boolean;
  /** Corriger un versement (montant / moyen / date) */
  canCorrectPaymentPlanPayments: boolean;

  // Permissions Fidélité carte (Pass)
  canViewLoyaltyPass: boolean;
  /** Paramétrer le programme / règles Pass */
  canManageLoyaltyPass: boolean;

  // Permissions Packs catalogue
  canViewCatalogPacks: boolean;
  canManageCatalogPacks: boolean;
  
  // Permissions Marketing (selon le secteur)
  canViewMarketing: boolean;
  canCreateMarketing: boolean;
  canUpdateMarketing: boolean;
  canDeleteMarketing: boolean;
  canViewMessagesClients: boolean;
  canSendMessagesClients: boolean;
  canUpdateMessagesClients: boolean;
  canManageMessagesClientsSettings: boolean;
  
  // Permissions Rendez-vous (selon le secteur)
  canViewAppointments: boolean;
  canCreateAppointments: boolean;
  canUpdateAppointments: boolean;
  canDeleteAppointments: boolean;
  canViewAppointmentRequests: boolean;  // Demandes de RDV (page /appointment-requests)

  // Permissions Ramassage (pressing)
  canViewPickups: boolean;
  canManagePickups: boolean;
  canUpdatePickupStatus: boolean;
  /** Valider un ramassage = ouvrir l’enregistrement de vente (forfait prestation). */
  canValidatePickup: boolean;
  
  // Permissions Stock - chaque feature pilotée par sa permission (plus de canViewInventory)
  canViewProducts: boolean;
  canViewCategories: boolean;
  canCreateProducts: boolean;
  canUpdateProducts: boolean;
  canDeleteProducts: boolean;
  
  canCreateCategories: boolean;
  canUpdateCategories: boolean;
  canDeleteCategories: boolean;
  
  canViewStockMovements: boolean;
  canCreateStockMovements: boolean;
  canUpdateStockMovements: boolean;
  canDeleteStockMovements: boolean;
  canViewInventoryLosses: boolean;
  canCreateInventoryLosses: boolean;
  canViewExternalDispatches: boolean;
  canCreateExternalDispatches: boolean;
  canUpdateExternalDispatches: boolean;
  canDeleteExternalDispatches: boolean;
  canViewExternalDestinations: boolean;
  canCreateExternalDestinations: boolean;
  canUpdateExternalDestinations: boolean;
  canDeleteExternalDestinations: boolean;
  
  canViewProductSales: boolean;
  canCreateProductSales: boolean;
  canUpdateProductSales: boolean;
  canDeleteProductSales: boolean;
  
  canViewCashReports: boolean;          // Rapports de caisse (ventes + prestations par jour)
  canViewAdvancedCashReports: boolean;  // Rapports avancés (filtres employé, catégories, regroupements PDF)
  canViewInvoicing: boolean;            // Voir Paramètres → Facturation
  canUpdateInvoicing: boolean;          // Modifier format, logo, cachet, signature
  canViewInventoryReports: boolean;    // Rapports d'inventaire (dashboard / stats stock)

  // Permissions Fabrication (ordres de fabrication)
  canViewManufacturingOrders: boolean;
  canCreateManufacturingOrders: boolean;
  canUpdateManufacturingOrders: boolean;
  canCloseManufacturingOrders: boolean; // Clôturer un ordre + appliquer les mouvements de stock
  canDeleteManufacturingOrders: boolean;

  // Permissions Inventory Audit (inventaires physiques)
  canViewInventoryAudit: boolean;      // Voir les sessions d'inventaire
  canCreateInventoryAudit: boolean;    // Créer nouvelles sessions
  canUpdateInventoryAudit: boolean;
  canDeleteInventoryAudit: boolean;
  canCountInventory: boolean;          // Effectuer les comptages
  canValidateInventory: boolean;       // Valider les écarts et appliquer ajustements
  canViewInventoryAuditReports: boolean; // Rapports d'inventaires physiques

  // Permissions Suppliers (selon le secteur)
  canViewSuppliers: boolean;
  canCreateSuppliers: boolean;
  canUpdateSuppliers: boolean;
  canDeleteSuppliers: boolean;
  
  canViewDeliveries: boolean;
  canCreateDeliveries: boolean;
  canUpdateDeliveries: boolean;
  canDeleteDeliveries: boolean;
  
  canViewSupplierReports: boolean;

   // Permissions Comptabilité
   canViewAccounting: boolean;
   // ✅ NOUVEAU : Permission de visualisation des dépenses
   canViewExpenses: boolean;
   // ✅ NOUVELLES : Permissions granulaires Expenses
   canViewLargeExpenses: boolean; // Voir les dépenses marquées "Grosse"
   canCreateExpenses: boolean;
   canUpdateExpenses: boolean;
   canDeleteExpenses: boolean;

   // Notes de frais (workflow employé -> validation -> dépense + paie)
   canViewExpenseNotes: boolean;
   canCreateExpenseNotes: boolean;
   canViewAllExpenseNotes: boolean;
   canApproveExpenseNotes: boolean;

  /** Payer la licence Nauvatek (échéances créées par le CSM / Super Admin) */
  canViewLicencePayment: boolean; // Voir la page Paiement (consultation seule)
  canPayLicence: boolean;

  // Indicateur de performance (objectifs mensuels + prime variable)
  canViewPerformanceObjectives: boolean;
  canCreatePerformanceObjectives: boolean;
  canUpdatePerformanceObjectives: boolean;
  canViewAllPerformanceObjectives: boolean;
  canValidatePerformanceObjectives: boolean;

  // Permissions Paies et Salaires
  canViewPayrolls: boolean;              // Voir les fiches de paie
  canCreatePayrolls: boolean;            // Créer/générer des fiches de paie
  canUpdatePayrolls: boolean;            // Modifier/valider des fiches de paie
  canDeletePayrolls: boolean;            // Supprimer des fiches de paie
  canViewSalaryAdjustments: boolean;     // Voir les ajustements salariaux
  canCreateSalaryAdjustments: boolean;   // Créer des ajustements salariaux
  canUpdateSalaryAdjustments: boolean;   // Modifier des ajustements salariaux
  canApproveSalaryAdjustments: boolean;  // Approuver/rejeter des ajustements

  // Permissions Référents
  canViewReferents: boolean;
  canCreateReferents: boolean;
  canUpdateReferents: boolean;
  canDeleteReferents: boolean;

  // Permissions Activité (rapports d'activité + pipeline + opportunités)
  canViewActivityReports: boolean;       // Voir les rapports d'activité (les siens ou selon canViewAll)
  canCreateActivityReports: boolean;     // Créer/valider son propre rapport
  canViewAllActivityReports: boolean;    // Voir tous les rapports de l'équipe / du business
  // Configuration pipeline (Paramètres > Configuration pipeline)
  canViewPipelines: boolean;             // Voir la configuration des pipelines
  canCreatePipelines: boolean;           // Créer un pipeline
  canUpdatePipelines: boolean;           // Modifier un pipeline
  canDeletePipelines: boolean;           // Supprimer un pipeline
  // Opportunités (Activité > Opportunités)
  canViewSalesPipeline: boolean;         // Voir la page Opportunités (pipeline commercial)
  canCreateOpportunities: boolean;       // Créer des opportunités
  canUpdateOpportunities: boolean;       // Modifier des opportunités
  canDeleteOpportunities: boolean;       // Supprimer des opportunités

  // Types de clients (sous-page Opportunités / config business)
  canViewClientTypes: boolean;
  canCreateClientTypes: boolean;
  canUpdateClientTypes: boolean;
  canDeleteClientTypes: boolean;

  // Types d'activité (sous-page Rapports d'activité / config business)
  canViewActivityTypes: boolean;
  canCreateActivityTypes: boolean;
  canUpdateActivityTypes: boolean;
  canDeleteActivityTypes: boolean;

  // Catégories d'opportunités (sous-page Opportunités / config business)
  canViewOpportunityCategories: boolean;
  canCreateOpportunityCategories: boolean;
  canUpdateOpportunityCategories: boolean;
  canDeleteOpportunityCategories: boolean;

  // Types / catégories de dépenses (sous-page Dépenses / config business)
  canViewExpenseCategories: boolean;
  canCreateExpenseCategories: boolean;
  canUpdateExpenseCategories: boolean;
  canDeleteExpenseCategories: boolean;

  // Permissions Super Admin
  canManageBusinesses: boolean;
  canCreateBusinesses: boolean;
  canViewAllBusinesses: boolean;
}

// ✅ SUPPRIMÉ : UserRole et ROLE_PERMISSIONS hardcodés
// Les rôles sont maintenant des profils personnalisés stockés dans la collection roleProfiles
// Les permissions sont définies dans RoleProfile.permissions

// ✅ NOUVEAU : Permissions par défaut pour créer les profils système
// Ces permissions seront utilisées lors de la création automatique des profils système
export const DEFAULT_ADMIN_PERMISSIONS: RolePermissions = {
  canViewDashboard: true,
  canViewEmployees: true,
  canCreateEmployees: true,
  canUpdateEmployees: true,
  canDeleteEmployees: true,
  canViewRoleProfiles: true,
  canCreateRoleProfiles: true,
  canUpdateRoleProfiles: true,
  canDeleteRoleProfiles: true,
  canViewServices: true,
  canCreateServices: true,
  canUpdateServices: true,
  canDeleteServices: true,
  canViewPrestations: true,
  canCreatePrestations: true,
  canUpdatePrestations: true,
  canDeletePrestations: true,
  canVoidSales: true,
  canCorrectSales: true,
  canIssueSaleCredits: true,
  canManageUsers: true,
  canViewClients: true,
  canCreateClients: true,
  canUpdateClients: true,
  canDeleteClients: true,

  canViewPaymentPlans: true,
  canCreatePaymentPlans: true,
  canUpdatePaymentPlans: true,
  canCancelPaymentPlans: true,
  canCompletePaymentPlans: true,
  canAddPaymentPlanPayments: true,
  canVoidPaymentPlanPayments: true,
  canCorrectPaymentPlanPayments: true,
  canViewLoyaltyPass: true,
  canManageLoyaltyPass: true,
  canViewCatalogPacks: true,
  canManageCatalogPacks: true,
  canViewMarketing: true,
  canCreateMarketing: true,
  canUpdateMarketing: true,
  canDeleteMarketing: true,
  canViewMessagesClients: true,
  canSendMessagesClients: true,
  canUpdateMessagesClients: true,
  canManageMessagesClientsSettings: true,
  canViewAppointments: true,
  canCreateAppointments: true,
  canUpdateAppointments: true,
  canDeleteAppointments: true,
  canViewAppointmentRequests: true,
  canViewPickups: true,
  canManagePickups: true,
  canUpdatePickupStatus: true,
  canValidatePickup: true,
  canViewProducts: true,
  canViewCategories: true,
  canCreateProducts: true,
  canUpdateProducts: true,
  canDeleteProducts: true,
  canCreateCategories: true,
  canUpdateCategories: true,
  canDeleteCategories: true,
  canViewStockMovements: true,
  canCreateStockMovements: true,
  canUpdateStockMovements: true,
  canDeleteStockMovements: true,
  canViewInventoryLosses: true,
  canCreateInventoryLosses: true,
  canViewExternalDispatches: true,
  canCreateExternalDispatches: true,
  canUpdateExternalDispatches: true,
  canDeleteExternalDispatches: true,
  canViewExternalDestinations: true,
  canCreateExternalDestinations: true,
  canUpdateExternalDestinations: true,
  canDeleteExternalDestinations: true,
  canViewProductSales: true,
  canCreateProductSales: true,
  canUpdateProductSales: true,
  canDeleteProductSales: true,
  canViewCashReports: true,
  canViewAdvancedCashReports: true,
  canViewInvoicing: true,
  canUpdateInvoicing: true,
  canViewInventoryReports: true,

  canViewManufacturingOrders: true,
  canCreateManufacturingOrders: true,
  canUpdateManufacturingOrders: true,
  canCloseManufacturingOrders: true,
  canDeleteManufacturingOrders: true,
  canViewInventoryAudit: true,
  canCreateInventoryAudit: true,
  canUpdateInventoryAudit: true,
  canDeleteInventoryAudit: true,
  canCountInventory: true,
  canValidateInventory: true,
  canViewInventoryAuditReports: true,
  canViewSuppliers: true,
  canCreateSuppliers: true,
  canUpdateSuppliers: true,
  canDeleteSuppliers: true,
  canViewDeliveries: true,
  canCreateDeliveries: true,
  canUpdateDeliveries: true,
  canDeleteDeliveries: true,
  canViewSupplierReports: true,
  canViewAccounting: true,
  canViewExpenses: true,
  canViewLargeExpenses: true,
  canCreateExpenses: true,
  canUpdateExpenses: true,
  canDeleteExpenses: true,
  canViewExpenseCategories: true,
  canCreateExpenseCategories: true,
  canUpdateExpenseCategories: true,
  canDeleteExpenseCategories: true,
  canViewExpenseNotes: true,
  canCreateExpenseNotes: true,
  canViewAllExpenseNotes: true,
  canApproveExpenseNotes: true,

  // Paiement licence : par défaut un admin/superadmin peut consulter et payer
  canViewLicencePayment: true,
  canPayLicence: true,

  canViewPerformanceObjectives: true,
  canCreatePerformanceObjectives: true,
  canUpdatePerformanceObjectives: true,
  canViewAllPerformanceObjectives: true,
  canValidatePerformanceObjectives: true,
  canViewPayrolls: true,
  canCreatePayrolls: true,
  canUpdatePayrolls: true,
  canDeletePayrolls: true,
  canViewSalaryAdjustments: true,
  canCreateSalaryAdjustments: true,
  canUpdateSalaryAdjustments: true,
  canApproveSalaryAdjustments: true,
  canViewReferents: true,
  canCreateReferents: true,
  canUpdateReferents: true,
  canDeleteReferents: true,
  canViewActivityReports: true,
  canCreateActivityReports: true,
  canViewAllActivityReports: true,
  canViewPipelines: true,
  canCreatePipelines: true,
  canUpdatePipelines: true,
  canDeletePipelines: true,
  canViewSalesPipeline: true,
  canCreateOpportunities: true,
  canUpdateOpportunities: true,
  canDeleteOpportunities: true,
  canViewClientTypes: true,
  canCreateClientTypes: true,
  canUpdateClientTypes: true,
  canDeleteClientTypes: true,
  canViewActivityTypes: true,
  canCreateActivityTypes: true,
  canUpdateActivityTypes: true,
  canDeleteActivityTypes: true,
  canViewOpportunityCategories: true,
  canCreateOpportunityCategories: true,
  canUpdateOpportunityCategories: true,
  canDeleteOpportunityCategories: true,
  canManageBusinesses: false,
  canCreateBusinesses: false,
  canViewAllBusinesses: false,
};

/** Toutes les permissions à true (y compris multi-tenant) — réservé au super admin côté UI */
export const SUPER_ADMIN_FULL_PERMISSIONS: RolePermissions = {
  ...DEFAULT_ADMIN_PERMISSIONS,
  canManageBusinesses: true,
  canCreateBusinesses: true,
  canViewAllBusinesses: true,
};

